import { addDoc, collection } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import type { ChecklistItem, ChecklistWorkflowStatus } from '../types/schema'
import { writeChecklistUpdate } from './checklistWriter'
import {
  buildProofApprovalUpdates,
  buildProofRejectionUpdates,
} from './checklistProofSubmissionEffects'
import { updateChecklistWorkflowStatus } from './checklistStatusWriter'
import { applyStripboardDelta } from './stripboardDeltaService'
import type { BoardStatus } from '../types/stripboard'

const extractReviewMetadata = (updates: Record<string, unknown>): Record<string, unknown> => {
  const metadata = { ...updates }
  delete metadata.workflowStatus
  delete metadata.boardStatus
  delete metadata.isCompleted
  delete metadata.completedAt
  delete metadata.completedById
  delete metadata.completedByName
  return metadata
}

const resolveCurrentLane = (item: {
  boardStatus?: BoardStatus | null
  workflowStatus?: ChecklistWorkflowStatus | null
}): BoardStatus => {
  if (item.boardStatus) return item.boardStatus
  if (item.workflowStatus === 'ASSESSING' || item.workflowStatus === 'ONGOING') return 'IN_PROGRESS'
  if (item.workflowStatus === 'COMPLETED' || item.workflowStatus === 'NOT_REQUIRED') return 'COMPLETE'
  return 'TODO'
}

export interface ProofReviewer {
  uid: string
  displayName: string
}

/**
 * Approve the current proof submission. Moves the item to COMPLETED and
 * stamps the review-metadata fields. Only the item's `proofApproverId` is
 * allowed by rules to call this (enforced via `isChecklistProofReviewUpdate`).
 */
export const approveChecklistProof = async (params: {
  item: Pick<ChecklistItem, 'id' | 'workspaceId' | 'title'> & Partial<Pick<ChecklistItem, 'checklistType' | 'proofPeriodKey' | 'workflowStatus' | 'boardStatus' | 'recordVersion' | 'previousWorkflowStatus'>>
  approver: ProofReviewer
  pageKey?: string | null
}): Promise<void> => {
  const updates = buildProofApprovalUpdates({ approver: params.approver })
  const isRepeating = params.item.checklistType === 'DAILY' || params.item.checklistType === 'WEEKLY'
  const periodKey = params.item.proofPeriodKey ?? params.pageKey ?? null

  if (isRepeating && periodKey) {
    const reviewMetadata = extractReviewMetadata(updates)
    await writeChecklistUpdate(
      params.item.id,
      reviewMetadata,
      { uid: params.approver.uid, displayName: params.approver.displayName },
      { pageKey: periodKey },
    )
    await updateChecklistWorkflowStatus({
      item: params.item,
      workflowStatus: 'COMPLETED',
      actor: { id: params.approver.uid, name: params.approver.displayName },
      periodKey,
      periodBoardStatus: resolveCurrentLane(params.item),
      runDelta: applyStripboardDelta,
    })
  } else {
    await writeChecklistUpdate(
      params.item.id,
      { ...updates, isCompleted: true, boardStatus: 'COMPLETE' },
      { uid: params.approver.uid, displayName: params.approver.displayName },
      ...(periodKey ? [{ pageKey: periodKey }] : []),
    )
  }
}

/**
 * Reject the current proof submission. Reverts the item to its previous
 * pre-ASSESSING status (or ONGOING), keeps the proof file for audit, and
 * posts the rejection note as a checklist comment so the assignee sees it
 * inline in the discussion thread.
 */
export const rejectChecklistProof = async (params: {
  item: Pick<ChecklistItem, 'id' | 'workspaceId' | 'title' | 'previousWorkflowStatus' | 'parentContainerId'>
    & Partial<Pick<ChecklistItem, 'checklistType' | 'proofPeriodKey' | 'workflowStatus' | 'boardStatus' | 'recordVersion'>>
  approver: ProofReviewer
  note: string
  pageKey?: string | null
}): Promise<void> => {
  const note = params.note.trim()
  if (!note) throw new Error('A rejection note is required.')
  const updates = buildProofRejectionUpdates({
    approver: params.approver,
    note,
    item: { previousWorkflowStatus: params.item.previousWorkflowStatus },
  })
  const isRepeating = params.item.checklistType === 'DAILY' || params.item.checklistType === 'WEEKLY'
  const periodKey = params.item.proofPeriodKey ?? params.pageKey ?? null

  if (isRepeating && periodKey) {
    const reviewMetadata = extractReviewMetadata(updates)
    await writeChecklistUpdate(
      params.item.id,
      reviewMetadata,
      { uid: params.approver.uid, displayName: params.approver.displayName },
      { pageKey: periodKey },
    )
    await updateChecklistWorkflowStatus({
      item: params.item,
      workflowStatus: (updates.workflowStatus as ChecklistWorkflowStatus) ?? 'ONGOING',
      actor: { id: params.approver.uid, name: params.approver.displayName },
      periodKey,
      periodBoardStatus: resolveCurrentLane(params.item),
      runDelta: applyStripboardDelta,
    })
  } else {
    await writeChecklistUpdate(
      params.item.id,
      { ...updates, isCompleted: false, boardStatus: 'IN_PROGRESS' },
      { uid: params.approver.uid, displayName: params.approver.displayName },
      ...(periodKey ? [{ pageKey: periodKey }] : []),
    )
  }
  await addDoc(collection(firestoreDb, 'checklist_comments'), {
    checklistItemId: params.item.id,
    parentContainerId: params.item.parentContainerId ?? null,
    workspaceId: params.item.workspaceId,
    userId: params.approver.uid,
    userName: params.approver.displayName,
    text: `Proof rejected: ${note}`,
    timestamp: new Date().toISOString(),
    parentCommentId: null,
    mentionedUserIds: [],
    mentionedUsers: [],
    reactions: {},
    attachments: [],
    attachmentCount: 0,
    editedAt: null,
    deletedAt: null,
    source: 'PROOF_REJECTION',
  })
}
